import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import { Upload, FileSearch, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FORMATS = [
  { v: "", label: "Auto-detect" },
  { v: "nessus", label: "Nessus (.nessus XML)" },
  { v: "nmap", label: "Nmap XML" },
  { v: "burp", label: "Burp Suite (JSON/XML)" },
  { v: "nuclei", label: "Nuclei JSON/JSONL" },
  { v: "zap", label: "OWASP ZAP JSON" },
  { v: "qualys", label: "Qualys XML" },
  { v: "csv", label: "Generic CSV" },
];

const ImportFindings = () => {
  const { activeId, active } = useEngagement();
  const [file, setFile] = useState(null);
  const [format, setFormat] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const loadJobs = useCallback(async () => {
    if (!activeId) return;
    try {
      const { data } = await api.get(`/engagements/${activeId}/import-jobs`);
      setJobs(data);
    } catch (err) {
      console.error("[import] load jobs failed:", err);
    }
  }, [activeId]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const upload = async () => {
    if (!file) {
      toast.error("Select a file first");
      return;
    }
    if (!activeId || activeId === "demo-default") {
      toast.error("Switch to a real client engagement before importing (Demo is read-only)");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (format) fd.append("format", format);
      const { data } = await api.post(`/engagements/${activeId}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.findings_count} findings (${data.new_assets} new assets)`);
      await loadJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (ev) => {
    ev.preventDefault();
    setDrag(false);
    if (ev.dataTransfer.files?.[0]) setFile(ev.dataTransfer.files[0]);
  };

  return (
    <div className="p-6 max-w-5xl">
      <PageHeader
        tag="Engagement / Findings Import"
        title="Import Findings"
        subtitle="Upload scanner outputs from Nessus / Nmap / Burp / Nuclei / ZAP / Qualys / CSV. Findings are normalized into the active engagement's vulnerability + asset inventory."
      />

      {(!active || active.id === "demo-default") && (
        <div className="csx-card border-amber-500/40 bg-amber-500/5 p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs">
            <div className="text-amber-300 uppercase tracking-widest font-bold">Demo engagement is read-only</div>
            <div className="text-slate-400 mt-1">
              You're currently viewing the <span className="font-mono text-amber-300">Demo Environment</span>. To import real client findings, create or switch to a client engagement from the header.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 csx-card p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-3">Upload Scanner Output</div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            data-testid="import-dropzone"
            className={[
              "border-2 border-dashed p-8 text-center cursor-pointer transition-all",
              drag ? "border-cyan-400 bg-cyan-500/5" : "border-slate-700 hover:border-slate-500",
            ].join(" ")}
          >
            <Upload className="h-8 w-8 mx-auto text-cyan-400 mb-3" />
            {file ? (
              <div>
                <div className="text-sm text-slate-100 font-mono">{file.name}</div>
                <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
              </div>
            ) : (
              <div className="text-sm text-slate-400">
                Drop scanner output file here, or <span className="text-cyan-300">click to browse</span>
                <div className="text-[10px] text-slate-500 mt-2">
                  .nessus · .xml · .json · .jsonl · .csv — up to 25 MB
                </div>
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              data-testid="import-file-input"
              accept=".nessus,.xml,.json,.jsonl,.csv,.txt"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <div className="mt-4">
            <span className="text-[10px] uppercase tracking-widest text-slate-400">Format</span>
            <select
              data-testid="import-format"
              className="w-full mt-1 bg-slate-950 border border-slate-800 px-3 py-2 text-sm"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              {FORMATS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
            </select>
          </div>

          <TButton
            data-testid="import-submit"
            className="mt-4 w-full"
            icon={busy ? Loader2 : Upload}
            onClick={upload}
            disabled={!file || busy}
          >
            {busy ? "Ingesting..." : "Import Findings"}
          </TButton>

          {result && (
            <div className="mt-4 border border-emerald-500/40 bg-emerald-500/5 p-4 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5" />
              <div className="text-xs space-y-1">
                <div className="text-emerald-300 uppercase tracking-widest font-bold">Ingest complete</div>
                <div className="text-slate-300">Format: <span className="font-mono text-cyan-300">{result.format}</span></div>
                <div className="text-slate-300">Findings: <span className="font-mono text-cyan-300">{result.findings_count}</span></div>
                <div className="text-slate-300">New assets: <span className="font-mono text-cyan-300">{result.new_assets}</span></div>
                <div className="text-slate-500 mt-2">View them under Risk Scoring or Asset Discovery (engagement-scoped).</div>
              </div>
            </div>
          )}
        </div>

        <div className="csx-card p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 mb-3">
            <FileSearch className="h-3 w-3" /> Import History
          </div>
          {jobs.length === 0 ? (
            <div className="text-xs text-slate-500">No imports yet for this engagement.</div>
          ) : (
            <ul className="space-y-2 text-xs">
              {jobs.map((j) => (
                <li key={j.id} className="border border-slate-800 p-2.5 bg-slate-950/40">
                  <div className="font-mono text-slate-200 truncate">{j.filename}</div>
                  <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                    <span>{j.format} · {j.findings_count} findings</span>
                    <span>{new Date(j.at).toLocaleDateString()}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-5 pt-5 border-t border-slate-800 text-[10px] text-slate-500 leading-relaxed">
            <div className="uppercase tracking-widest text-slate-400 mb-1">Tip</div>
            Findings are de-duplicated by (asset + title) within an engagement. Re-importing the same file is safe — only new findings appear.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportFindings;
